import { AccountInfo, AuthenticationResult, IPublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { app, authentication } from '@microsoft/teams-js';
import jwt_decode from 'jwt-decode';
import { TrackJS } from 'trackjs';

import { apiUrl, microsoftRequest } from '@shared/services/EnvironmentVariables';
import { isInOutlook, isInTeams } from '@shared/utils/TeamsUtils';

import { TabCoordinator } from './tabCoordination';
import {
  AcquireGraphOptions,
  AcquireSessionOptions,
  AcquireSessionSource,
  GraphTokens,
  NoMsalAccountError,
  SESSION_SCOPE_KEY,
  SessionTokens,
  buildIdentityHint,
  buildScopeKey,
} from './types';

const SSO_CACHE_MAX_TTL_MS = 24 * 60 * 60 * 1000;
// Must be >= msalConfig.system.tokenRenewalOffsetSeconds (currently 300). When
// the gateway cache misses we fall through to MSAL acquireTokenSilent; MSAL
// will refresh from AAD if within its renewal offset, so aligning the two
// thresholds lets MSAL's proactive refresh actually fire. It also keeps the
// frontend's "still valid" window comfortably ahead of the backend's 60s
// isJwtExpired buffer (see JwtService.java), eliminating spurious
// "Access token expired" refresh logs during the latency-wide race window.
const SSO_CACHE_HEADROOM_MS = 5 * 60 * 1000;
const MANUAL_COOLDOWN_MS = 2_000;
const AUTO_COOLDOWN_MS = 60_000;

interface SsoCacheEntry {
  tokens: SessionTokens;
  evictAt: number;
}

interface CooldownEntry {
  lastAt: number;
}

interface SsoResponseBody {
  accessToken: string;
  idToken: string;
  refreshTokenExpiresAt?: number;
}

export interface AcquireTokenGatewayDeps {
  msal: IPublicClientApplication;
  tabCoord: TabCoordinator;
  fetchSso?: (token: string, scopes: string[]) => Promise<SsoResponseBody>;
}

export class AcquireTokenGateway {
  private readonly msal: IPublicClientApplication;
  private readonly tabCoord: TabCoordinator;
  private readonly fetchSso: (token: string, scopes: string[]) => Promise<SsoResponseBody>;

  private readonly inflight = new Map<string, Promise<unknown>>();
  private readonly ssoCache = new Map<string, SsoCacheEntry>();
  private readonly cooldowns = new Map<string, CooldownEntry>();
  private teamsUserIdPromise: Promise<string | undefined> | null = null;
  private cachedOid: string | undefined;

  constructor(deps: AcquireTokenGatewayDeps) {
    this.msal = deps.msal;
    this.tabCoord = deps.tabCoord;
    this.fetchSso = deps.fetchSso ?? defaultFetchSso;
  }

  async acquireSession(opts: AcquireSessionOptions): Promise<SessionTokens> {
    const identity = await this.resolveIdentity();
    const cacheKey = `${SESSION_SCOPE_KEY}::${identity}`;
    const inflightKey = `${cacheKey}::session`;

    if (!opts.forceRefresh) {
      const cached = this.readSsoCache(cacheKey);
      if (cached) return cached;
    }

    const existing = this.inflight.get(inflightKey) as Promise<SessionTokens> | undefined;
    if (existing) return existing;

    const promise = this.doAcquireSession(opts, identity, cacheKey);
    this.inflight.set(inflightKey, promise);
    try {
      return await promise;
    } finally {
      this.inflight.delete(inflightKey);
    }
  }

  async acquireGraph(opts: AcquireGraphOptions): Promise<GraphTokens> {
    const identity = await this.resolveIdentity({ homeAccountId: opts.account?.homeAccountId });
    const scopeKey = buildScopeKey(opts.scopes);
    const inflightKey = `${scopeKey}::${identity}::graph`;

    if (!this.canPromptForScope(scopeKey, opts.source)) {
      throw new GraphCooldownError(`requestCalendarAccess on cooldown for source=${opts.source}`);
    }

    const existing = this.inflight.get(inflightKey) as Promise<GraphTokens> | undefined;
    if (existing) return existing;

    const promise = this.doAcquireGraph(opts, identity, scopeKey);
    this.inflight.set(inflightKey, promise);
    try {
      const result = await promise;
      this.markCooldownConsumed(scopeKey);
      return result;
    } finally {
      this.inflight.delete(inflightKey);
    }
  }

  /**
   * Best-effort silent fetch — never triggers a popup or redirect. Returns null
   * on cache miss, network failure, or interaction-required. Used by the auto
   * step-up path so that auto callers can never escalate to a popup.
   */
  async tryGetGraphTokensSilently(scopes: string[]): Promise<GraphTokens | null> {
    if (isInTeams()) {
      try {
        await app.initialize();
        const teamsToken = await authentication.getAuthToken({ silent: true } as any);
        const body = await this.fetchSso(teamsToken, scopes);
        if (!tokenContainsScopes(body.accessToken, scopes)) return null;
        return {
          accessToken: body.accessToken,
          scopes,
          expiresOn: extractExpiry(body.accessToken),
        };
      } catch {
        return null;
      }
    }

    const account = this.resolveAccount();
    if (!account) return null;

    try {
      const result = await this.msal.acquireTokenSilent({
        ...microsoftRequest.login,
        scopes,
        redirectUri: `${window.location.origin}/blank.html`,
        account,
        forceRefresh: false,
      });
      return toGraphTokens(result, scopes);
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) return null;
      return null;
    }
  }

  /**
   * Seed the in-memory session cache from tokens obtained outside the silent
   * flow (e.g. authentication.authenticate popup). Future getAuthHeaders calls
   * via acquireSession({source:'auto'}) will hit this cache without round-trips.
   */
  seedSession(tokens: SessionTokens): void {
    const identity = tokens.oid
      ? buildIdentityHint({ oid: tokens.oid })
      : buildIdentityHint({});
    const cacheKey = `${SESSION_SCOPE_KEY}::${identity}`;
    this.writeSsoCache(cacheKey, tokens, undefined);
    if (tokens.oid) this.cachedOid = tokens.oid;
  }

  invalidate(target: { kind: 'session' } | { kind: 'graph'; scopes: string[] } | { kind: 'all' }): void {
    if (target.kind === 'session' || target.kind === 'all') {
      this.ssoCache.clear();
    }
    if (target.kind === 'all') {
      this.inflight.clear();
      this.cooldowns.clear();
      this.cachedOid = undefined;
      this.teamsUserIdPromise = null;
    }
    if (target.kind === 'graph') {
      const scopeKey = buildScopeKey(target.scopes);
      this.cooldowns.delete(scopeKey);
    }
  }

  dispose(): void {
    this.tabCoord.dispose();
    this.inflight.clear();
    this.ssoCache.clear();
    this.cooldowns.clear();
    this.cachedOid = undefined;
    this.teamsUserIdPromise = null;
  }

  resolveAccount(opts?: { homeAccountId?: string; account?: AccountInfo }): AccountInfo | undefined {
    if (opts?.account) {
      this.msal.setActiveAccount(opts.account);
      return opts.account;
    }
    if (opts?.homeAccountId) {
      const match = this.msal.getAllAccounts().find((a) => a.homeAccountId === opts.homeAccountId);
      if (match) {
        this.msal.setActiveAccount(match);
        return match;
      }
    }
    const active = this.msal.getActiveAccount();
    if (active) return active;
    const first = this.msal.getAllAccounts()[0];
    if (first) {
      this.msal.setActiveAccount(first);
      return first;
    }
    return undefined;
  }

  async getTeamsUserId(): Promise<string | undefined> {
    if (!this.teamsUserIdPromise) {
      this.teamsUserIdPromise = (async () => {
        try {
          await app.initialize();
          const ctx = await app.getContext();
          return ctx.user?.id;
        } catch (err) {
          TrackJS?.track('[authMachine] teams id resolve failed');
          return undefined;
        }
      })();
    }
    return this.teamsUserIdPromise;
  }

  private async doAcquireSession(
    opts: AcquireSessionOptions,
    identity: string,
    cacheKey: string,
  ): Promise<SessionTokens> {
    const claim = await this.tabCoord.claim({
      kind: 'sso',
      oid: identity,
      scopes: microsoftRequest.login.scopes,
    });
    if (claim.kind === 'await') {
      // Tokens acquired by another tab: same origin, same expiry rules as our own acquisition.
      this.writeSsoCache(cacheKey, claim.tokens, undefined, !isInTeams());
      this.cachedOid = claim.tokens.oid;
      return claim.tokens;
    }

    try {
      const inTeams = isInTeams();
      const tokens = inTeams
        ? await this.acquireSessionViaTeamsSso()
        : await this.acquireSessionViaMsal(opts);

      // Only MSAL-acquired (web) sessions are capped at the access token's own expiry:
      // MSAL can renew those silently. Teams / Outlook sessions keep the long cache and
      // rely on the backend's server-side refresh, because in Outlook the silent SSO
      // path cannot re-establish a session and a cache miss would force a re-auth popup.
      this.writeSsoCache(cacheKey, tokens, undefined, !inTeams);
      this.cachedOid = tokens.oid;
      this.tabCoord.publishResult('sso', claim.nonce, tokens);
      return tokens;
    } catch (err: any) {
      this.tabCoord.publishFailure('sso', claim.nonce, err?.errorCode);
      throw err;
    }
  }

  private async doAcquireGraph(
    opts: AcquireGraphOptions,
    identity: string,
    scopeKey: string,
  ): Promise<GraphTokens> {
    const claim = await this.tabCoord.claim({
      kind: 'graph',
      oid: identity,
      scopeKey,
    });
    if (claim.kind === 'await') {
      return claim.tokens;
    }

    try {
      const tokens = isInTeams()
        ? await this.acquireGraphViaTeamsSso(opts.scopes, opts.interactive ?? false)
        : await this.acquireGraphViaMsal(opts.scopes, opts.account, opts.interactive ?? false);

      this.tabCoord.publishResult('graph', claim.nonce, tokens);
      return tokens;
    } catch (err: any) {
      this.tabCoord.publishFailure('graph', claim.nonce, err?.errorCode);
      throw err;
    }
  }

  private async acquireSessionViaMsal(opts: AcquireSessionOptions): Promise<SessionTokens> {
    const account = this.resolveAccount();
    if (!account) {
      throw new NoMsalAccountError();
    }
    try {
      const result = await this.msal.acquireTokenSilent({
        ...microsoftRequest.login,
        redirectUri: `${window.location.origin}/blank.html`,
        account,
        forceRefresh: opts.forceRefresh ?? false,
      });
      return toSessionTokens(result);
    } catch (err: unknown) {
      if (!(err instanceof InteractionRequiredAuthError)) throw err;
      // MSAL's cached refresh token is dead (typical after ~24h+ inactivity).
      // Try a hidden-iframe SSO against login.microsoftonline.com using the
      // user's still-alive Microsoft browser session cookie. This recovers
      // the "I left the tab open over the weekend" case without sending the
      // user back to a login screen.
      TrackJS?.track('[AcquireTokenGateway] acquireTokenSilent failed, escalating to ssoSilent');
      try {
        const result = await this.msal.ssoSilent({
          ...microsoftRequest.login,
          redirectUri: `${window.location.origin}/blank.html`,
          loginHint: account.username,
          account,
        });
        return toSessionTokens(result);
      } catch (ssoErr: unknown) {
        // ssoSilent's hidden iframe can't reach the Microsoft session cookie when
        // third-party cookies are blocked (Safari ITP, strict Firefox) or the MS
        // session is gone. For an interactive caller, escalate to a top-level
        // acquireTokenRedirect: a full-page navigation carries the Microsoft
        // session cookie first-party (immune to ITP), so if that session is
        // alive the user returns to /login/azure re-authenticated without a
        // password and never lands on the app's /login screen. Background callers
        // (auto / agent-401-retry) must not navigate the tab away mid-action, so
        // they re-throw and let the next interactive entry point recover.
        if (!isInteractiveSource(opts.source)) throw ssoErr;
        TrackJS?.track('[AcquireTokenGateway] ssoSilent failed, escalating to acquireTokenRedirect');
        await this.msal.acquireTokenRedirect({
          ...microsoftRequest.login,
          redirectUri: `${window.location.origin}/login/azure`,
          loginHint: account.username,
          account,
        });
        // Unreachable — acquireTokenRedirect navigates the browser away. Throw to
        // satisfy the type system; control never returns from the line above.
        throw new Error('Awaiting Microsoft redirect for session re-auth');
      }
    }
  }

  private async acquireSessionViaTeamsSso(): Promise<SessionTokens> {
    await app.initialize();
    const body = await this.exchangeTeamsTokenWithRetry(microsoftRequest.login.scopes);
    if (!tokenContainsScopes(body.accessToken, microsoftRequest.login.scopes)) {
      throw new ConsentRequiredError('Session token missing requested scopes');
    }
    return ssoBodyToSessionTokens(body);
  }

  private async acquireGraphViaMsal(scopes: string[], accountHint?: { homeAccountId: string }, interactive = false): Promise<GraphTokens> {
    const account = this.resolveAccount({ homeAccountId: accountHint?.homeAccountId });
    if (!account) {
      throw new Error('No MSAL account available for graph acquisition');
    }
    try {
      const result = await this.msal.acquireTokenSilent({
        ...microsoftRequest.login,
        scopes,
        redirectUri: `${window.location.origin}/blank.html`,
        account,
        forceRefresh: false,
      });
      return toGraphTokens(result, scopes);
    } catch (err: unknown) {
      if (interactive && isConsentRequiredErr(err)) {
        // Escalate to redirect-based consent rather than a popup. Reasons:
        //   1. Popups get blocked by browsers / extensions silently — the user
        //      sees nothing and assumes the button is broken.
        //   2. Popups require the redirectUri (e.g. /blank.html) to be
        //      registered in Azure; that's an extra config concern.
        //   3. /login/azure is already a registered redirect URI, so this
        //      "just works" wherever login does.
        // After Microsoft redirects back to /login/azure, MSAL caches the
        // newly-consented token; the auto-acquire path on reload sees the
        // calendar scopes silently and flips calendar='ready'.
        // acquireTokenRedirect navigates the browser away — control never
        // returns from this call, so the function below is unreachable. Throw
        // a sentinel error after a no-op timeout to satisfy the type system.
        await this.msal.acquireTokenRedirect({
          ...microsoftRequest.login,
          scopes,
          redirectUri: `${window.location.origin}/login/azure`,
          account,
          prompt: 'consent',
        });
        // Unreachable — the redirect navigates away. Throw to keep TS happy.
        throw new ConsentRequiredError('Awaiting Microsoft redirect for consent');
      }
      throw err;
    }
  }

  private async acquireGraphViaTeamsSso(scopes: string[], interactive = false): Promise<GraphTokens> {
    await app.initialize();

    // Outlook can never satisfy the silent getAuthToken path inside the iframe.
    // For interactive callers go straight to popup; for auto callers signal that
    // consent is required so the caller can no-op rather than retry forever.
    if (isInOutlook()) {
      if (!interactive) {
        throw new ConsentRequiredError('Graph token unavailable silently in Outlook');
      }
      return this.acquireGraphViaTeamsAuthPopup(scopes);
    }

    try {
      const body = await this.exchangeTeamsTokenWithRetry(scopes);
      if (!tokenContainsScopes(body.accessToken, scopes)) {
        throw new ConsentRequiredError('Graph token missing requested scopes');
      }
      return {
        accessToken: body.accessToken,
        scopes,
        expiresOn: extractExpiry(body.accessToken),
      };
    } catch (err: unknown) {
      if (interactive && isConsentRequiredErr(err)) {
        // Open the Teams auth-tab popup which performs an interactive OAuth
        // hand-off, then retry the /sso exchange — by that point the user
        // has consented to the scopes and the OBO call will succeed.
        await authentication.authenticate({
          url: `${window.location.origin}/login/tab-auth?scopes=${encodeURIComponent(scopes.join(' '))}`,
          width: 600,
          height: 535,
        });
        const retryBody = await this.exchangeTeamsTokenWithRetry(scopes);
        if (!tokenContainsScopes(retryBody.accessToken, scopes)) {
          throw new ConsentRequiredError('Graph token still missing scopes after consent popup');
        }
        return {
          accessToken: retryBody.accessToken,
          scopes,
          expiresOn: extractExpiry(retryBody.accessToken),
        };
      }
      throw err;
    }
  }

  private async acquireGraphViaTeamsAuthPopup(scopes: string[]): Promise<GraphTokens> {
    const response = await authentication.authenticate({
      url: `${window.location.origin}/login/tab-auth?scopes=${encodeURIComponent(scopes.join(' '))}`,
      width: 600,
      height: 535,
    });
    const popupTokens = JSON.parse(response) as {
      idToken: string;
      accessToken: string;
      expiresIn?: string;
    };
    return {
      accessToken: popupTokens.accessToken,
      scopes,
      expiresOn: popupTokens.expiresIn
        ? Date.now() + parseInt(popupTokens.expiresIn, 10) * 1000
        : extractExpiry(popupTokens.accessToken),
    };
  }

  private async exchangeTeamsTokenWithRetry(scopes: string[]): Promise<SsoResponseBody> {
    // In Outlook, force silent — without this teams-js opens an interactive
    // popup which both flashes and fails inside the sandboxed add-in iframe.
    // Teams keeps the default (it resolves silently anyway, but allows the SDK
    // to escalate if needed).
    const silent = isInOutlook();
    const teamsToken = await authentication.getAuthToken(silent ? ({ silent: true } as any) : {});
    try {
      return await this.fetchSso(teamsToken, scopes);
    } catch (err: any) {
      const code = err?.errorCode as string | undefined;
      const isAuthFailure = code === 'sso_401' || code === 'sso_403';
      const isExpired = typeof err?.message === 'string' && (
        err.message.includes('AADSTS500133') ||
        err.message.includes('token expired') ||
        err.message.includes('access token timed out')
      );
      if (!isAuthFailure && !isExpired) throw err;
      // Skip retry in Outlook — if silent failed once it'll fail again, and a
      // second attempt would just trigger another popup flash before fallback.
      if (silent) throw err;
      const freshToken = await authentication.getAuthToken({});
      return await this.fetchSso(freshToken, scopes);
    }
  }

  private async resolveIdentity(opts?: { homeAccountId?: string }): Promise<string> {
    // When the caller explicitly hints at an account, always resolve that account's
    // oid — never short-circuit on cachedOid which may belong to a different account.
    if (opts?.homeAccountId) {
      const account = this.msal.getAllAccounts().find((a) => a.homeAccountId === opts.homeAccountId);
      if (account) {
        const oid = (account.idTokenClaims as { oid?: string } | undefined)?.oid;
        if (oid) return buildIdentityHint({ oid });
        return buildIdentityHint({ homeAccountId: account.homeAccountId });
      }
    }
    if (this.cachedOid) {
      return buildIdentityHint({ oid: this.cachedOid });
    }
    const active = this.resolveAccount();
    if (active) {
      const oid = (active.idTokenClaims as { oid?: string } | undefined)?.oid;
      if (oid) {
        this.cachedOid = oid;
        return buildIdentityHint({ oid });
      }
      return buildIdentityHint({ homeAccountId: active.homeAccountId });
    }
    const teamsUserId = await this.getTeamsUserId();
    return buildIdentityHint({ teamsUserId });
  }

  private readSsoCache(cacheKey: string): SessionTokens | null {
    const entry = this.ssoCache.get(cacheKey);
    if (!entry) return null;
    if (Date.now() >= entry.evictAt) {
      this.ssoCache.delete(cacheKey);
      return null;
    }
    return entry.tokens;
  }

  private writeSsoCache(
    cacheKey: string,
    tokens: SessionTokens,
    refreshTokenExpiresAt: number | undefined,
    honourAccessTokenExpiry = false,
  ): void {
    const flatCap = Date.now() + SSO_CACHE_MAX_TTL_MS - SSO_CACHE_HEADROOM_MS;
    const refreshCap = refreshTokenExpiresAt ? refreshTokenExpiresAt - SSO_CACHE_HEADROOM_MS : flatCap;
    // The MS access token itself lives ~1h, far shorter than the 24h session
    // cap. Without this cap the cache keeps serving the expired access token
    // until the session cap, and the backend (MicrosoftJwtFilter) silently
    // refreshes it against Azure on every single request without ever handing
    // the new token back, which becomes a per-request Azure round trip.
    // Evicting at expiry-minus-headroom makes the next acquireSession fall
    // through to MSAL, whose renewal offset then refreshes the token silently.
    // Only applied where a silent renewal is actually possible (see caller).
    const accessCap = honourAccessTokenExpiry && Number.isFinite(tokens.expiresOn) && tokens.expiresOn > 0
      ? tokens.expiresOn - SSO_CACHE_HEADROOM_MS
      : flatCap;
    const evictAt = Math.min(flatCap, refreshCap, accessCap);
    this.ssoCache.set(cacheKey, { tokens, evictAt });
  }

  private canPromptForScope(scopeKey: string, source: 'auto' | 'manual'): boolean {
    const entry = this.cooldowns.get(scopeKey);
    if (!entry) return true;
    const cooldown = source === 'manual' ? MANUAL_COOLDOWN_MS : AUTO_COOLDOWN_MS;
    return Date.now() - entry.lastAt >= cooldown;
  }

  private markCooldownConsumed(scopeKey: string): void {
    this.cooldowns.set(scopeKey, { lastAt: Date.now() });
  }
}

export class GraphCooldownError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GraphCooldownError';
  }
}

class ConsentRequiredError extends Error {
  readonly errorCode = 'consent_required';
  constructor(message: string) {
    super(`Consent required: ${message}`);
    this.name = 'ConsentRequiredError';
  }
}

// User-driven / app-mount entry points where a full-page redirect is acceptable.
// Background sources ('auto', 'agent-401-retry') must never navigate the tab away.
function isInteractiveSource(source: AcquireSessionSource): boolean {
  return source === 'bootstrap' || source === 'login' || source === 'register';
}

export function isConsentRequiredErr(err: unknown): boolean {
  if (err instanceof ConsentRequiredError) return true;
  if (err instanceof InteractionRequiredAuthError) return true;
  if (err && typeof err === 'object') {
    const e = err as { errorCode?: string; message?: string };
    if (e.errorCode === 'consent_required') return true;
    if (e.message && (
      e.message.includes('AADSTS65001') ||
      e.message.includes('Consent required') ||
      e.message.includes('consent_required')
    )) return true;
  }
  return false;
}

function toSessionTokens(result: AuthenticationResult): SessionTokens {
  const claims = (result.idTokenClaims ?? {}) as { oid?: string };
  return {
    idToken: result.idToken,
    accessToken: result.accessToken,
    expiresOn: result.expiresOn ? result.expiresOn.getTime() : Date.now() + 60 * 60 * 1000,
    oid: claims.oid ?? '',
  };
}

function toGraphTokens(result: AuthenticationResult, requestedScopes: string[]): GraphTokens {
  return {
    accessToken: result.accessToken,
    scopes: result.scopes ?? requestedScopes,
    expiresOn: result.expiresOn ? result.expiresOn.getTime() : Date.now() + 60 * 60 * 1000,
  };
}

function ssoBodyToSessionTokens(body: SsoResponseBody): SessionTokens {
  const claims = decodeJwtClaims(body.accessToken) as { oid?: string; exp?: number };
  return {
    idToken: body.idToken,
    accessToken: body.accessToken,
    expiresOn: claims.exp ? claims.exp * 1000 : Date.now() + 60 * 60 * 1000,
    oid: claims.oid ?? '',
  };
}

function extractExpiry(token: string): number {
  const claims = decodeJwtClaims(token) as { exp?: number };
  return claims.exp ? claims.exp * 1000 : Date.now() + 60 * 60 * 1000;
}

function tokenContainsScopes(accessToken: string, scopes: string[]): boolean {
  const claims = decodeJwtClaims(accessToken) as { scp?: string };
  const tokenScopes = (claims.scp ?? '').toUpperCase().split(' ');
  const OIDC_META = ['OFFLINE_ACCESS', 'EMAIL', 'OPENID', 'PROFILE'];
  return scopes.every((s) => {
    const upper = s.toUpperCase();
    return OIDC_META.includes(upper) || tokenScopes.includes(upper);
  });
}

function decodeJwtClaims(token: string): Record<string, unknown> {
  try {
    return jwt_decode(token) as Record<string, unknown>;
  } catch {
    return {};
  }
}

async function defaultFetchSso(token: string, scopes: string[]): Promise<SsoResponseBody> {
  const response = await fetch(`${apiUrl}/sso`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, scopes }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    const error = new Error(`/sso ${response.status}: ${text.slice(0, 200)}`);
    (error as { errorCode?: string }).errorCode = `sso_${response.status}`;
    throw error;
  }
  return (await response.json()) as SsoResponseBody;
}
